/**
 * Professional Passport · Private View — Blueprint tranche AD-7
 * (Feb 10, 2026 · r4 doctrine-locked).
 *
 * Three surfaces (staged disclosure): Home → Timeline → Detail.
 * Read-only evidence view — no writes, no persistence of dismissal
 * state, no automatic keyboard focus anywhere on mount.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AD7_ACTIONS_SECTION_HEADING,
  AD7_ACTIONS_SUPPORTING_LINE,
  AD7_ACTIONS_BUTTON_LABEL,
  AD7_ACTIONS_NAVIGATION_TARGET,
} from "../lib/ad7ActionsCopy";
import {
  FIELD_SPECS,
  ACTOR_ROLE_LABELS,
  SOURCE_LEDGER_LABELS,
  PLACEMENT_PRIMARY_EVIDENCE,
  PLACEMENT_EVIDENCE_DETAILS,
  PLACEMENT_HIDDEN_FROM_V1,
  renderValue,
  renderEvidenceReferenceTail,
} from "../lib/ad7PresentationLabels";

const API = process.env.REACT_APP_BACKEND_URL;

function useProfessionalPassport(filters, cursor) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const params = { ...filters };
        if (cursor) params.cursor = cursor;
        const { data } = await axios.get(
          `${API}/api/passport/professional/private`,
          { params, withCredentials: true },
        );
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setState({ loading: false, error: { status, detail: err?.response?.data?.detail }, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [JSON.stringify(filters), cursor]);
  return state;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
  } catch { return ""; }
}

// ── Home ─────────────────────────────────────────────────
function Home({ data, onOpenTimeline }) {
  const { counts } = data;
  const empty = counts.presentable_total === 0;
  return (
    <section data-testid="passport-professional-home" className="space-y-4">
      <header className="space-y-1">
        <h1 data-testid="passport-title" className="text-2xl font-bold text-white">Professional Passport</h1>
        <p data-testid="passport-privacy-note" className="text-xs text-slate-400">
          Private to you. Not shared with learners, other instructors, or the public.
        </p>
        <p className="text-sm text-slate-300">
          A private, chronological view of professional evidence routed from lessons connected to your instructor account. Each entry shows who recorded it.
        </p>
      </header>
      {empty ? (
        <div data-testid="passport-empty-state" className="bg-slate-800 rounded-2xl border border-slate-700 p-4">
          <p className="text-sm text-slate-200">
            No professional evidence is available in your passport yet. Entries will appear when lawful lesson evidence is routed here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p data-testid="passport-evidence-count" className="text-sm text-slate-200">
            {counts.presentable_total === 1 ? "1 evidence entry in your passport." : `${counts.presentable_total} evidence entries in your passport.`}
          </p>
          {counts.latest_recorded_at && (
            <p data-testid="passport-latest-evidence-date" className="text-xs text-slate-400">
              Latest evidence: {formatDate(counts.latest_recorded_at)}.
            </p>
          )}
          <button
            type="button"
            data-testid="passport-open-timeline-btn"
            onClick={onOpenTimeline}
            className="h-11 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm"
          >
            Open Evidence Timeline
          </button>
        </div>
      )}

      {/* AD-7 r5 Linkage Amendment (Feb 20, 2026, operator-locked).
          Neutral secondary control routing the owning authenticated
          instructor to the AD-8 public-passport settings surface.
          Never rendered on the public page. No AD-8 state is read
          or displayed here — the button is purely navigational.
          Doctrine-pinned by TestAD7PassportActionsLinkage. */}
      <PassportActions />
    </section>
  );
}

// ── Passport actions (AD-7 r5) ──────────────────────────────
function PassportActions() {
  const navigate = useNavigate();
  return (
    <section
      data-testid="ad7-passport-actions"
      className="pt-6 mt-6 border-t border-slate-800 space-y-2"
    >
      <p className="text-[11px] uppercase tracking-widest text-slate-400">
        {AD7_ACTIONS_SECTION_HEADING}
      </p>
      <p className="text-sm text-slate-300 leading-relaxed">
        {AD7_ACTIONS_SUPPORTING_LINE}
      </p>
      <button
        type="button"
        data-testid="ad7-manage-public-passport-btn"
        onClick={() => navigate(AD7_ACTIONS_NAVIGATION_TARGET)}
        className="h-10 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-medium border border-slate-700"
      >
        {AD7_ACTIONS_BUTTON_LABEL}
      </button>
    </section>
  );
}

// ── Timeline ─────────────────────────────────────────────
function TimelineRow({ item, onOpen }) {
  const roleLabel = ACTOR_ROLE_LABELS[item.actor_role] || null;
  const sourceLabel = SOURCE_LEDGER_LABELS[item.source_ledger] || null;
  return (
    <button
      type="button"
      data-testid={`passport-timeline-item-${item.source_id}`}
      onClick={() => onOpen(item)}
      className="w-full text-left bg-slate-800 rounded-xl border border-slate-700 p-3 space-y-1 hover:border-slate-500"
    >
      <p className="text-sm font-bold text-white">{item.event_label}</p>
      <p className="text-xs text-slate-400">{formatDate(item.recorded_at)}</p>
      <div className="flex gap-2 flex-wrap text-[11px] text-slate-400">
        {roleLabel && <span>Recorded by: {roleLabel}</span>}
        {sourceLabel && <span>· {sourceLabel}</span>}
      </div>
    </button>
  );
}

function Timeline({ data, filters, onFilterChange, onOpenDetail, onBack, onLoadMore, hasFilters }) {
  const { items, counts, next_cursor, unsupported_summary } = data;
  return (
    <section data-testid="passport-timeline" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Evidence Timeline</h2>
        <button
          type="button"
          data-testid="passport-timeline-back"
          onClick={onBack}
          className="text-xs text-slate-400 underline underline-offset-4"
        >Back</button>
      </div>
      {unsupported_summary?.notice_required && (
        <div data-testid="passport-unsupported-notice" className="bg-amber-900/20 border border-amber-700/40 rounded-xl p-3">
          <p className="text-xs text-amber-100">
            Some professional evidence cannot currently be shown in this passport. It remains set aside under the evidence contract.
          </p>
        </div>
      )}
      {hasFilters && (
        <p className="text-[11px] text-slate-500">
          Showing {counts.filtered_total} of {counts.presentable_total}.
        </p>
      )}
      {items.length === 0 ? (
        <div data-testid="passport-timeline-empty" className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <p className="text-sm text-slate-300">
            {hasFilters
              ? "No evidence matches these filters. Adjust or clear them to see more."
              : "No professional evidence is available in your passport yet. Entries will appear when lawful lesson evidence is routed here."}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={`${it.source_ledger}-${it.source_id}-${it.recorded_at}`}>
              <TimelineRow item={it} onOpen={onOpenDetail} />
            </li>
          ))}
        </ul>
      )}
      {next_cursor && (
        <button
          type="button"
          data-testid="passport-timeline-load-more"
          onClick={() => onLoadMore(next_cursor)}
          className="w-full h-10 rounded-xl bg-slate-700 border border-slate-600 text-slate-100 text-sm font-medium"
        >Load more</button>
      )}
    </section>
  );
}

// ── Detail ───────────────────────────────────────────────
function Detail({ item, onBack }) {
  const specs = FIELD_SPECS[item.source_ledger] || {};
  const [showDetails, setShowDetails] = useState(false);
  const primary = [];
  const details = [];
  for (const [k, v] of Object.entries(item.projected_fields)) {
    const s = specs[k];
    if (!s || s.placement === PLACEMENT_HIDDEN_FROM_V1) continue;
    const rendered = renderValue(s, v);
    if (rendered === null) continue;
    (s.placement === PLACEMENT_PRIMARY_EVIDENCE ? primary : details).push([s.label, rendered]);
  }
  const roleLabel = ACTOR_ROLE_LABELS[item.actor_role];
  const sourceLabel = SOURCE_LEDGER_LABELS[item.source_ledger];
  return (
    <section data-testid="passport-detail" className="space-y-3">
      <button
        type="button"
        data-testid="passport-detail-back"
        onClick={onBack}
        className="text-xs text-slate-400 underline underline-offset-4"
      >Back</button>
      <p data-testid="passport-detail-event-label" className="text-lg font-bold text-white">{item.event_label}</p>
      <p data-testid="passport-detail-recorded-at" className="text-xs text-slate-400">{formatDate(item.recorded_at)}</p>
      <p data-testid="passport-detail-authorship" className="text-sm text-slate-200">Recorded by: {roleLabel}</p>
      {primary.length > 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-3 space-y-2" data-testid="passport-detail-projected-fields">
          {primary.map(([label, value]) => (
            <div key={label}>
              <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
              <p className="text-sm text-slate-100 whitespace-pre-wrap">{value}</p>
            </div>
          ))}
        </div>
      )}
      <p data-testid="passport-detail-source" className="text-xs text-slate-400">{sourceLabel}</p>
      <p data-testid="passport-detail-historical-note" className="text-[11px] text-slate-500 italic">
        This is a historical evidence record. It has not been scored, rated, or interpreted.
      </p>
      <button
        type="button"
        data-testid="passport-detail-evidence-details-toggle"
        onClick={() => setShowDetails((v) => !v)}
        className="text-xs text-slate-400 underline underline-offset-4"
      >
        {showDetails ? "Hide" : "Show"} evidence details
      </button>
      {showDetails && (
        <div data-testid="passport-detail-evidence-details" className="bg-slate-900/50 rounded-xl border border-slate-800 p-3 space-y-2">
          {details.map(([label, value]) => (
            <div key={label}>
              <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
              <p className="text-xs text-slate-300 whitespace-pre-wrap">{value}</p>
            </div>
          ))}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Evidence reference</p>
            <p data-testid="passport-detail-source-reference" className="text-xs text-slate-300">{renderEvidenceReferenceTail(item.source_id)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Source schema version</p>
            <p data-testid="passport-detail-source-schema-version" className="text-xs text-slate-300">{item.source_schema_version}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Routing rule</p>
            <p data-testid="passport-detail-routing-rule-id" className="text-xs text-slate-300">{item.routing_rule_id}</p>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Container ─────────────────────────────────────────────
export default function PassportProfessionalPrivate() {
  const [screen, setScreen] = useState("home");   // home | timeline | detail
  const [filters, setFilters] = useState({});
  const [cursor, setCursor] = useState(null);
  const [selected, setSelected] = useState(null);
  const { loading, error, data } = useProfessionalPassport(filters, cursor);
  const hasFilters = useMemo(() => Object.values(filters).some(Boolean), [filters]);

  if (loading) return <div className="p-6 text-slate-400">Loading passport…</div>;
  if (error) {
    if (error.status === 401) return <div className="p-6 text-slate-300">Please sign in to view your passport.</div>;
    if (error.status === 403) return <div data-testid="passport-role-denied" className="p-6 text-slate-300">{error.detail || "This passport is not available to your role."}</div>;
    if (error.status === 422) return <div className="p-6 text-slate-300">{error.detail || "Invalid request."}</div>;
    return <div className="p-6 text-slate-300">Passport evidence is temporarily unavailable. Please try again shortly.</div>;
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4">
      {screen === "home" && (
        <Home data={data} onOpenTimeline={() => setScreen("timeline")} />
      )}
      {screen === "timeline" && (
        <Timeline
          data={data}
          filters={filters}
          onFilterChange={setFilters}
          onOpenDetail={(it) => { setSelected(it); setScreen("detail"); }}
          onBack={() => setScreen("home")}
          onLoadMore={(c) => setCursor(c)}
          hasFilters={hasFilters}
        />
      )}
      {screen === "detail" && selected && (
        <Detail item={selected} onBack={() => setScreen("timeline")} />
      )}
    </div>
  );
}
